import { useMemo } from "react";
import { cn } from "~/lib/utils";
import { ImageLoader } from "~/components/ImageLoader";
import { RWA_COIN_ICON } from "~/consts";
import * as Analytics from "~/modules/media_citations/analytics";
import { MediaCitation } from "../../types";

// // // //

// Publishers with fewer citations than this are merged into an "All Others"
// facet at the bottom of the publisher list
export const MIN_PUBLISHER_CITATIONS = 3;

// Sentinel publisherFilter value representing the merged "All Others" facet
export const ALL_OTHER_PUBLISHERS = "__all_other_publishers__";

// Publishers merged into "All Others", determined from the FULL citation set
// so facet membership stays stable while other filters narrow the counts
export function getSmallPublisherIds(citations: MediaCitation[]): Set<string> {
    const counts = new Map<string, number>();

    citations.forEach((citation) => {
        counts.set(
            citation.publisher_id,
            (counts.get(citation.publisher_id) ?? 0) + 1,
        );
    });

    return new Set(
        [...counts.entries()]
            .filter(([, count]) => count < MIN_PUBLISHER_CITATIONS)
            .map(([id]) => id),
    );
}

interface PublisherFacet {
    id: string;
    name: string;
    iconUrl: string;
    count: number;
}

interface Props {
    citations: MediaCitation[];
    // Publisher counts reflect the active type filter + search query
    publisherFacetCitations: MediaCitation[];
    publisherFilter: string | null;
    onPublisherFilterChange: (publisherId: string | null) => void;
}

// // // //

function FacetRow(props: {
    label: string;
    iconUrl: string;
    count: number;
    selected: boolean;
    onClick: () => void;
}) {
    const { label, iconUrl, count, selected, onClick } = props;

    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                "flex w-full items-center justify-between gap-2 rounded-md px-3 py-1.75 text-left text-sm transition-colors",
                {
                    "bg-blue-50 text-primary": selected,
                    "text-secondary-foreground hover:bg-blue-50": !selected,
                },
            )}
        >
            <span className="flex min-w-0 items-center gap-1.5">
                <ImageLoader
                    src={iconUrl || RWA_COIN_ICON}
                    alt={`${label} icon`}
                    className="size-3 shrink-0 rounded object-contain"
                    fallbackSrc={RWA_COIN_ICON}
                />
                <span className="truncate">{label}</span>
            </span>
            <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                {count}
            </span>
        </button>
    );
}

// // // //

export function CitationsFilterSidebar(props: Props) {
    const {
        citations,
        publisherFacetCitations,
        publisherFilter,
        onPublisherFilterChange,
    } = props;

    const smallPublisherIds = useMemo(
        () => getSmallPublisherIds(citations),
        [citations],
    );

    const publisherFacets: PublisherFacet[] = useMemo(() => {
        const byId = new Map<string, PublisherFacet>();

        publisherFacetCitations.forEach((citation) => {
            const facet = byId.get(citation.publisher_id);

            if (facet) {
                facet.count += 1;
                return;
            }

            byId.set(citation.publisher_id, {
                id: citation.publisher_id,
                name: citation.publisher.name,
                iconUrl: citation.publisher.icon_url,
                count: 1,
            });
        });

        return [...byId.values()];
    }, [publisherFacetCitations]);

    const mainPublisherFacets = useMemo(() => {
        const main = publisherFacets
            .filter((facet) => !smallPublisherIds.has(facet.id))
            .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

        // Keep a selected publisher in the list even when the other filters
        // narrow its count to zero, so the active filter stays visible
        if (
            publisherFilter !== null &&
            publisherFilter !== ALL_OTHER_PUBLISHERS &&
            !smallPublisherIds.has(publisherFilter) &&
            !main.some((facet) => facet.id === publisherFilter)
        ) {
            const citation = citations.find(
                (c) => c.publisher_id === publisherFilter,
            );

            if (citation) {
                main.push({
                    id: publisherFilter,
                    name: citation.publisher.name,
                    iconUrl: citation.publisher.icon_url,
                    count: 0,
                });
            }
        }

        return main;
    }, [publisherFacets, smallPublisherIds, publisherFilter, citations]);

    const allOthersCount = publisherFacets
        .filter((facet) => smallPublisherIds.has(facet.id))
        .reduce((sum, facet) => sum + facet.count, 0);

    // NOTE - horizontal padding lives on the label + scroller rather than the
    // card so the scroll-fade overlays can reach the card edges, and the list
    // runs from a border under the label to the card's bottom edge (no bottom
    // padding) so the fade's hard edges land on structural edges instead of
    // floating in white padding. overflow-hidden clips it to the corners.
    return (
        <aside className="hidden flex-col gap-5.5 overflow-hidden rounded-xl border bg-card shadow-2xs lg:sticky lg:top-20 lg:flex">
            <div>
                <p className="border-b py-4 text-sm font-normal pl-5.5 text-muted-foreground">
                    Publishers
                </p>
                <div className="scroll-fade [--scroll-fade-inset:0.75rem] hide-scrollbar max-h-120 min-h-120 overflow-y-auto px-3">
                    {/* NOTE - vertical padding + flex gap live on this inner wrapper,
                        not the scroller: padding on a scroll container constrains
                        its sticky children (the scroll-fade overlays) and leaves an
                        unfaded strip at each end */}
                    <div className="flex flex-col gap-0.5 py-2">
                        {mainPublisherFacets.map((facet) => (
                            <FacetRow
                                key={facet.id}
                                label={facet.name}
                                iconUrl={facet.iconUrl}
                                count={facet.count}
                                selected={publisherFilter === facet.id}
                                onClick={() => {
                                    const nextValue =
                                        publisherFilter === facet.id
                                            ? null
                                            : facet.id;

                                    Analytics.trackChangePublisherFilter({
                                        publisher: facet.name,
                                        selected: nextValue !== null,
                                    });

                                    onPublisherFilterChange(nextValue);
                                }}
                            />
                        ))}
                        {allOthersCount > 0 && (
                            <FacetRow
                                label="All Others"
                                iconUrl={RWA_COIN_ICON}
                                count={allOthersCount}
                                selected={
                                    publisherFilter === ALL_OTHER_PUBLISHERS
                                }
                                onClick={() => {
                                    const nextValue =
                                        publisherFilter === ALL_OTHER_PUBLISHERS
                                            ? null
                                            : ALL_OTHER_PUBLISHERS;

                                    Analytics.trackChangePublisherFilter({
                                        publisher: "All Others",
                                        selected: nextValue !== null,
                                    });

                                    onPublisherFilterChange(nextValue);
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>
        </aside>
    );
}
